import RenderEntity, { RENDER_ENTITY_FORMAT3 } from 'ui-component/renders/RenderEntity';
import { lazy } from 'react';

// material-ui
import { Stack, Typography, Tooltip } from '@mui/material';

// third-party
import { format, parseJSON } from 'date-fns';
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from '@mui/x-data-grid-pro';

// project imports
import { appState, appGridActions } from 'store/behaviors/loader';
import capabilities from './capabilities';
import { CustomDetailPanelToggle } from 'ui-component/custom/AppDataGrid/Types/EntitySubGrid';

// assets
import FolderTwoToneIcon from '@mui/icons-material/FolderTwoTone';
import AddCircleTwoToneIcon from '@mui/icons-material/AddCircleTwoTone';
import { renderUser } from 'ui-component/fields/EntitySelect/admin/UserSelect';

// lazy loaded components
const BusinessOpportunityTypeSelect = lazy(() => import('ui-component/fields/EntitySelect/workmgmt/BusinessOpportunityTypeSelect'));
const UserSelect = lazy(() => import('ui-component/fields/EntitySelect/admin/UserSelect'));

const initialState = {
    ...appState(capabilities),
    colDef: [
        {
            ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
            renderCell: (params) => <CustomDetailPanelToggle id={params.id} value={params.value} />
        },
        {
            field: 'actions',
            type: 'actions',
            description: '',
            align: 'center',
            hideable: false,
            sortable: false,
            resizable: false,
            disableReorder: true,
            label: 'ui-comp-table-actions',
            getActions: (params, permissionData, onClickActions) => {
                const gridActions = appGridActions(capabilities, params?.row, permissionData, onClickActions);

                permissionData?.forEach((perm) => {
                    switch (perm) {
                        case 'tasks-canAdd':
                            if (params?.row?.deletedAt) {
                                return;
                            }

                            gridActions.push({
                                key: `actionaddtask-row${params?.row?.id}`,
                                icon: <AddCircleTwoToneIcon fontSize="small" />,
                                label: 'ui-comp-button-add',
                                position: 0,
                                showInMenu: false,
                                onClick: () => {
                                    if (
                                        'onClickRowActionAddTask' in (onClickActions || {}) &&
                                        typeof onClickActions.onClickRowActionAddTask === 'function'
                                    ) {
                                        onClickActions.onClickRowActionAddTask(params?.row);
                                    }
                                }
                            });
                            break;
                        default: // do nothing
                    }
                });

                return gridActions;
            }
        },
        {
            field: 'id',
            type: 'number',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            label: 'traits-appentity-id'
        },
        {
            field: 'name',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            flex: 1,
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={params.value}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <FolderTwoToneIcon color="secondary" sx={{ fontSize: '0.875rem' }} />
                            <Typography
                                variant="inherit"
                                color="inherit"
                                sx={{
                                    overflow: 'hidden',
                                    display: 'block',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                    // ':hover': { color: 'primary.main' }
                                    // cursor: 'pointer'
                                }}
                            >
                                {params.value}
                            </Typography>
                        </Stack>
                    </Tooltip>
                ),
            label: 'traits-appentityisdescribable-name'
        },
        {
            field: 'description',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: false,
            resizable: true,
            label: 'traits-appentityisdescribable-description'
        },
        {
            field: 'businessOpportunityType',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            renderCell: (params) => (
                <RenderEntity
                    entityName="businessOpportunityType"
                    renderPattern="<name>"
                    renderTooltipPattern="[<acronym>] <name>"
                    renderFormat={RENDER_ENTITY_FORMAT3}
                    {...params}
                />
            ),
            label: 'entity-workmgmt-businessopportunitytype'
        },
        {
            field: 'createdAt',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentity-createdat'
        },
        {
            field: 'createdBy',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            renderCell: (params) => renderUser(params?.value, 'fullName'),
            label: 'traits-appentity-createdby'
        },
        {
            field: 'updatedAt',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentity-updatedat'
        },
        {
            field: 'updatedBy',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            renderCell: (params) => renderUser(params?.value, 'fullName'),
            label: 'traits-appentity-updatedby'
        }
    ],
    dataGridState: {
        columns: {
            columnVisibilityModel: {
                id: false,
                name: true,
                description: false,
                businessOpportunityType: true,
                createdAt: true,
                createdBy: true,
                updatedAt: false,
                updatedBy: false
            },
            orderedFields: [
                'actions',
                'id',
                'name',
                'description',
                'businessOpportunityType',
                'createdAt',
                'createdBy',
                'updatedAt',
                'updatedBy'
            ],
            dimensions: {
                actions: { width: 60 },
                id: { width: 100 },
                name: { width: 150 },
                description: { width: 100 },
                businessOpportunityType: { width: 120 },
                createdAt: { width: 100 },
                createdBy: { width: 100 },
                updatedAt: { width: 100 },
                updatedBy: { width: 100 }
            }
        },
        sorting: {
            sortModel: [{ field: 'id', sort: 'asc' }]
        }
    },
    filterDef: [
        {
            filterType: 'string',
            field: { fieldName: 'name', fieldType: 'simple' },
            label: 'traits-appentityisdescribable-name'
        },
        {
            filterType: 'string',
            field: { fieldName: 'description', fieldType: 'simple' },
            label: 'traits-appentityisdescribable-description'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'businessOpportunityTypeId', fieldType: 'simple' },
            label: 'entity-workmgmt-businessopportunitytype',
            renderField: (params) => (
                <BusinessOpportunityTypeSelect key="filterTplBusinessOpportunity-businessOpportunityType" multiple {...params} />
            )
        },
        {
            filterType: 'dateTime',
            field: { fieldName: 'createdAt', fieldType: 'simple' },
            label: 'traits-appentity-createdat'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'createdBy', fieldType: 'simple', outputFieldName: 'createdById' },
            label: 'traits-appentity-createdby',
            renderField: (params) => <UserSelect key="filterTplBusinessOpportunity-createdBy" multiple displayMode="fullName" {...params} />
        },
        {
            filterType: 'dateTime',
            field: { fieldName: 'updatedAt', fieldType: 'simple' },
            label: 'traits-appentity-updatedat'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'updatedBy', fieldType: 'simple', outputFieldName: 'updatedById' },
            label: 'traits-appentity-updatedby',
            renderField: (params) => <UserSelect key="filterTplBusinessOpportunity-updatedBy" multiple displayMode="fullName" {...params} />
        }
    ],
    quickSearchFilterDef: {
        placeholder: 'ui-comp-quicksearchplaceholder-tplbusinessopportunity',
        fields: [
            { fieldName: 'name', fieldType: 'simple' },
            { fieldName: 'description', fieldType: 'simple' },
            { fieldName: 'businessOpportunityTypeAcronym', fieldType: 'simple' },
            { fieldName: 'businessOpportunityTypeName', fieldType: 'simple' }
        ]
    },
    sortingDef: [
        { fieldName: 'id', fieldType: 'simple' },
        { fieldName: 'name', fieldType: 'simple' },
        { fieldName: 'businessOpportunityTypeId', fieldType: 'simple' },
        { fieldName: 'createdAt', fieldType: 'simple' },
        { fieldName: 'createdBy', fieldType: 'simple' },
        { fieldName: 'updatedAt', fieldType: 'simple' },
        { fieldName: 'updatedBy', fieldType: 'simple' }
    ]
};

export default initialState;
